import { useCallback, useMemo, useRef, useState } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { Checkbox, Stack } from '@provider-portal/design-common/kalep/components';
import { red } from '@mui/material/colors';

import { Promotion, PromotionDuration } from '../../types';
import { PromotionFooter } from '../PromotionFooter/PromotionFooter';
import {
  PromotionSummaryFC,
  PromotionSummaryFCProps,
} from '../PromotionSummary/PromotionSummaryFC';
import { Asterisk } from '@provider-portal/common/components/Asterisk/Asterisk';

export interface AddPromotionOverviewFCProps {
  strings: {
    title: string;
    back: string;
    publish: string;
    tncWithConsent: string;
    promotionVisibilityDisclaimer: string;
  } & PromotionSummaryFCProps['strings'];
  promotion: Promotion;
  duration: PromotionDuration;
  selectedMenuItemNames: string;
  hasConsented?: boolean;
  onClickPublish: () => void;
  onClickBack: () => void;
}

export const AddPromotionOverviewFC = ({
  strings,
  promotion,
  duration,
  selectedMenuItemNames,
  hasConsented,
  onClickPublish,
  onClickBack,
}: AddPromotionOverviewFCProps) => {
  const [isAgreeWithTaC, setIsAgreeWithTaC] = useState(false);
  const [errorStyle, setErrorStyle] = useState(undefined);

  const canPublish = hasConsented || isAgreeWithTaC;

  const buttonRef = useRef(null);

  const checkBoxChanged = useCallback(
    (_: unknown, checked: boolean) => {
      setIsAgreeWithTaC(checked);
    },
    [setIsAgreeWithTaC],
  );
  const onPublishButtonClick = useCallback(() => {
    if (canPublish && buttonRef.current && !buttonRef.current.disabled) {
      buttonRef.current.disabled = true;
      onClickPublish();
    }
  }, [canPublish, onClickPublish]);

  const onErrorOccurred = useCallback(() => {
    setErrorStyle({ color: red[500] });
  }, [setErrorStyle]);

  const footer = useMemo(() => {
    const checkBox = hasConsented ? undefined : (
      <Checkbox
        checked={isAgreeWithTaC}
        onChange={checkBoxChanged}
        sx={{ ...errorStyle, marginRight: '8px' }}
      />
    );
    return (
      <Stack gap="2rem">
        {/* The T&C link is AZ-only, and Regular promotions have no AZ variant, so it's always hidden here. */}
        <PromotionFooter
          text={strings.tncWithConsent}
          preText={checkBox}
          shouldShowOmnibusDisclaimer
          hideTacLink
        >
          <Box mr={1}>
            <Button color="secondary" variant="contained" onClick={onClickBack}>
              {strings.back}
            </Button>
          </Box>
          <Box>
            <span
              role="button"
              tabIndex={0}
              onKeyDown={canPublish ? onPublishButtonClick : onErrorOccurred}
              onClick={canPublish ? onPublishButtonClick : onErrorOccurred}
              style={{ display: 'block' }}
            >
              <Button variant="contained" color="primary" disabled={!canPublish} ref={buttonRef}>
                {strings.publish}
              </Button>
            </span>
          </Box>
        </PromotionFooter>
        <Box mr={1}>
          <Asterisk lines={[strings.promotionVisibilityDisclaimer]} />
        </Box>
      </Stack>
    );
  }, [
    checkBoxChanged,
    errorStyle,
    isAgreeWithTaC,
    hasConsented,
    canPublish,
    onClickBack,
    onErrorOccurred,
    onPublishButtonClick,
    strings.tncWithConsent,
    strings.back,
    strings.publish,
    strings.promotionVisibilityDisclaimer,
  ]);

  return (
    <Box>
      <Box marginBottom={2}>
        <Typography variant="h6">{strings.title}</Typography>
      </Box>
      <PromotionSummaryFC
        promotion={promotion}
        duration={duration}
        strings={strings}
        footer={footer}
        selectedMenuItemNames={selectedMenuItemNames}
        selectedProductIds={[]}
        exclusionTags={[]}
        hideMenuEditor
        onMenuItemsUpdate={null}
      />
    </Box>
  );
};
